/**
 * Session-expiry handling. Wired into the app-wide apiClient via the
 * `onAuthError` option: when any API call comes back 401 with a
 * session-gone code (token revoked by password change, logout in another
 * tab, server-side pruning, …) we clear the persisted auth state and do a
 * FULL navigation to /login.
 *
 * Full navigation (location.assign, not router navigate) is deliberate:
 * it drops every piece of in-memory state seeded from the dead session —
 * TanStack Query caches, zustand stores, the editor's working copy — so
 * the login page starts clean instead of flashing stale data.
 */
import { useAuthStore } from "../stores/auth";

/**
 * Build the login URL for an expired session. `redirect` carries the
 * page the user was on so login can bounce them back. Only same-origin
 * absolute paths are accepted (no `//evil.com` open-redirect, no
 * external URLs); anything else falls back to plain /login.
 */
export function buildLoginUrl(currentPathAndSearch: string): string {
  const params = new URLSearchParams({ expired: "1" });
  const safe =
    currentPathAndSearch.startsWith("/") &&
    !currentPathAndSearch.startsWith("//");
  if (safe && currentPathAndSearch !== "/" && !currentPathAndSearch.startsWith("/login")) {
    params.set("redirect", currentPathAndSearch);
  }
  return `/login?${params.toString()}`;
}

/**
 * Clear the persisted session and bounce to /login. Idempotent: when the
 * user is already on an auth page (several parallel queries can all fail
 * with 401 at once) we only clear — no redirect loop.
 */
export function handleSessionExpired(
  navigate: (url: string) => void = (url) => window.location.assign(url),
): void {
  useAuthStore.getState().clear();

  if (window.location.pathname.startsWith("/login")) return;

  navigate(buildLoginUrl(window.location.pathname + window.location.search));
}
